import type { ParserPipelineFlags } from './flags';
import type { ParseMetrics, ParseNaturalLanguageFiltersResult } from './types';

const METRICS_NAMESPACE = 'nl-filter-parser';
const DEBUG_PREFIX = `[${METRICS_NAMESPACE}]`;

/**
 * Silent in production because smart search parses on every debounced
 * keystroke, and in tests because the traces drown out the assertions.
 */
export function isParserDebugEnabled(): boolean {
  return process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';
}

/**
 * Development-only trace of a parse: the query going in, and what it resolved
 * to coming out. Logs wherever the parser runs — with the LLM steps on, that is
 * the API route, so these land in the server terminal rather than the browser.
 */
export function debugParserInput(query: string, flags: ParserPipelineFlags): void {
  if (!isParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.debug(DEBUG_PREFIX, 'input', { query, flags });
}

export function debugParserOutput(result: ParseNaturalLanguageFiltersResult | null): void {
  if (!isParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.debug(
    DEBUG_PREFIX,
    'output',
    result === null
      ? null
      : {
          filters: result.filters,
          tags: result.tags,
          unparsed: result.unparsed,
          matchedFields: result.metrics.matchedFields,
          durationMs: Number(result.metrics.durationMs.toFixed(1)),
        }
  );
}

/**
 * Default metrics sink — structured console info for performance analysis.
 * Callers can replace via `onMetrics` or pass `false` to silence.
 */
export function defaultMetricsLogger(metrics: ParseMetrics): void {
  // eslint-disable-next-line no-console
  console.info(`[${METRICS_NAMESPACE}]`, {
    matchCoverage: Number(metrics.matchCoverage.toFixed(4)),
    residualRate: Number(metrics.residualRate.toFixed(4)),
    durationMs: Number(metrics.durationMs.toFixed(3)),
    confidence: metrics.confidence === null ? null : Number(metrics.confidence.toFixed(4)),
    matchedFields: metrics.matchedFields,
    residual: metrics.residual,
    query: metrics.query,
    flags: metrics.flags,
  });
}

export function emitMetrics(
  metrics: ParseMetrics,
  onMetrics: ((metrics: ParseMetrics) => void) | false | undefined
): void {
  if (onMetrics === false) return;
  const sink = onMetrics ?? defaultMetricsLogger;
  sink(metrics);
}
